import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, 
  X, 
  ExternalLink, 
  MapPin, 
  Calendar, 
  Users,
  Building2,
  Filter,
  ChevronDown,
  ArrowLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioRow = ({ company, onClick, index }) => {
  return (
    <motion.button
      type="button"
      aria-label={`View ${company.name} portfolio details`}
      onClick={() => onClick(company)}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      className="group w-full cursor-pointer text-left"
      data-testid={`portfolio-row-${company.company_id}`}
    >
      <div className="bg-white border-b border-gray-100 hover:bg-gray-50/50 transition-all duration-200 py-5 px-4 md:px-6">
        <div className="flex items-start md:items-center gap-4 md:gap-6">
          {/* Logo */}
          <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-100">
            <img
              src={company.logo_url?.startsWith('/') ? `${BACKEND_URL}${company.logo_url}` : company.logo_url || `https://ui-avatars.com/api/?name=${company.name}&background=C8473A&color=fff`}
              alt={company.logo_alt?.trim() || company.name}
              className="w-full h-full object-contain p-1.5"
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${company.name}&background=C8473A&color=fff`;
              }}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0 flex flex-col md:justify-center min-h-20 md:min-h-24 py-0.5">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 mb-1.5 md:mb-1">
              <h2 className="text-2xl md:text-3xl font-semibold text-[#211F1F] group-hover:text-[#C8473A] transition-colors truncate leading-tight">
                {company.name}
              </h2>
              {company.status === 'exited' && (
                <Badge className="bg-[#C8473A] text-white text-[10px] w-fit">EXITED</Badge>
              )}
            </div>
            
            {/* Description - hidden on mobile */}
            <p className="hidden md:block text-gray-500 text-base leading-relaxed line-clamp-1 mb-2">
              {company.short_description || company.long_description?.substring(0, 150) || company.description || ''}
            </p>
            
            {/* Sectors */}
            <div className="flex flex-wrap items-center gap-1.5">
              {company.sectors?.slice(0, 3).map((sector, idx) => (
                <span 
                  key={idx} 
                  className="inline-block px-2.5 py-0.5 bg-[#C8473A]/10 text-yn-brand-on-tint text-sm font-medium rounded-full"
                >
                  {sector}
                </span>
              ))}
              {company.sectors?.length > 3 && (
                <span className="text-xs text-muted-light">+{company.sectors.length - 3}</span>
              )}
            </div>
          </div>

          {/* Right side info - hidden on mobile */}
          <div className="hidden lg:flex items-center gap-8 flex-shrink-0">
            {company.location && (
              <div className="text-sm text-gray-500 min-w-[120px]">
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                  {company.location}
                </span>
              </div>
            )}
            <div className="text-sm text-muted-light min-w-[80px]">
              {company.invested_year}
            </div>
          </div>

          {/* Arrow */}
          <div className="flex-shrink-0 self-center md:self-auto">
            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#C8473A] group-hover:translate-x-1 transition-all" aria-hidden="true" />
          </div>
        </div>
      </div>
    </motion.button>
  );
};

const getCompanyFundIds = (company) => {
  if (!company) return [];
  if (Array.isArray(company.fund_ids) && company.fund_ids.length) return company.fund_ids;
  if (company.fund_id) return [company.fund_id];
  return [];
};

const CompanyModal = ({ company, isOpen, onClose, fundNameById }) => {
  if (!company) return null;

  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-modal">
        <DialogHeader>
          <div className="flex items-start gap-4">
            <div className="w-32 h-32 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0 border">
              <img
                src={company.logo_url?.startsWith('/') ? `${BACKEND_URL}${company.logo_url}` : company.logo_url || `https://ui-avatars.com/api/?name=${company.name}&background=C8473A&color=fff`}
                alt={company.logo_alt?.trim() || company.name}
                className="w-full h-full object-contain p-1.5"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${company.name}&background=C8473A&color=fff`;
                }}
              />
            </div>
            <div className="flex-1 flex flex-col justify-center h-32">
              <DialogTitle className="text-4xl font-bold text-[#211F1F] mb-3 leading-tight">
                {company.name}
              </DialogTitle>
              <div className="flex flex-wrap gap-2">
                {company.sectors?.map((sector, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-[#C8473A]/10 text-yn-brand-on-tint text-sm">
                    {sector}
                  </Badge>
                ))}
                {company.status === 'exited' && (
                  <Badge className="bg-[#C8473A] text-white text-sm">EXITED</Badge>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          {/* Description */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">About</h4>
            <p className="text-gray-600 leading-relaxed">
              {company.long_description || company.short_description || company.description || 'No description available'}
            </p>
          </div>

          {/* Funds */}
          {getCompanyFundIds(company).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Funds</h4>
              <div className="flex flex-wrap gap-2">
                {getCompanyFundIds(company).map((fid) => (
                  <Badge key={fid} variant="secondary" className="bg-[#C8473A]/10 text-yn-brand-on-tint text-sm">
                    {fundNameById?.get(fid) || fid}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            {company.location && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-[#C8473A]" aria-hidden="true" />
                <span className="text-gray-600">{company.location}</span>
              </div>
            )}
            {company.invested_year && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-[#C8473A]" aria-hidden="true" />
                <span className="text-gray-600">
                  Invested {MONTH_NAMES[company.invested_month - 1]} {company.invested_year}
                </span>
              </div>
            )}
            {company.patents_count > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-[#C8473A]" aria-hidden="true" />
                <span className="text-gray-600">{company.patents_count} Patents</span>
              </div>
            )}
          </div>

          {/* Founders */}
          {company.founders?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" aria-hidden="true" />
                Founders
              </h4>
              <div className="flex flex-wrap gap-2">
                {company.founders.map((founder, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    {founder.linkedin_url ? (
                      <a 
                        href={founder.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${founder.name} on LinkedIn (opens in new tab)`}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors"
                      >
                        {founder.name}
                      </a>
                    ) : (
                      <span className="px-3 py-1.5 bg-gray-100 rounded-full text-sm font-medium">
                        {founder.name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fund Team Members */}
          {company.team_members_details?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" aria-hidden="true" />
                YourNest Team
              </h4>
              <div className="flex flex-wrap gap-3">
                {company.team_members_details.map((member, idx) => (
                  <Link 
                    key={idx} 
                    to={`/team/${member.slug || member.member_id}`}
                    onClick={() => onClose()}
                    className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors"
                  >
                    {member.photo_url ? (
                      <img 
                        src={member.photo_url.startsWith('/') ? `${BACKEND_URL}${member.photo_url}` : member.photo_url}
                        alt={member.photo_alt || member.name}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${member.name}&background=C8473A&color=fff&size=32`;
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#C8473A]/10 flex items-center justify-center">
                        <span className="text-xs font-medium text-yn-brand-on-tint">{member.name?.charAt(0)}</span>
                      </div>
                    )}
                    <span className="text-sm font-medium hover:text-[#C8473A] transition-colors">{member.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Website Link */}
          {company.website_url && (
            <div className="pt-4 border-t">
              <a
                href={company.website_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${company.name} website (opens in new tab)`}
                className="inline-flex items-center gap-2 text-[#C8473A] font-medium hover:underline"
                data-testid="company-website-link"
              >
                Visit Website <ExternalLink className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PortfolioPage = () => {
  usePageTitle('Portfolio');
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFund, setSelectedFund] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSector, setSelectedSector] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fundNameById = useMemo(() => {
    const map = new Map();
    (filters?.funds || []).forEach((f) => map.set(f.fund_id, f.name));
    return map;
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, []);

  // Open company modal from ?open= query (company_id or company name from homepage testimonials)
  useEffect(() => {
    const openParam = searchParams.get('open');
    if (!openParam || companies.length === 0) return;
    const match = companies.find(
      (c) =>
        c.company_id === openParam ||
        c.name === openParam ||
        (c.name && c.name.toLowerCase().includes(openParam.toLowerCase()))
    );
    if (match) {
      setSelectedCompany(match);
      setSearchParams({}, { replace: true });
    }
  }, [companies, searchParams, setSearchParams]);

  const fetchData = async () => {
    try {
      const [companiesRes, filtersRes] = await Promise.all([
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/portfolio/filters`)
      ]);
      setCompanies(companiesRes.data);
      setFilters(filtersRes.data);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchTerm || 
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.sectors?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase())) ||
      company.short_description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const companyFundIds = getCompanyFundIds(company);
    const matchesFund = selectedFund === 'all' || companyFundIds.includes(selectedFund);
    const matchesLocation = selectedLocation === 'all' || company.location === selectedLocation;
    const matchesStatus = selectedStatus === 'all' || company.status === selectedStatus;
    const matchesSector = selectedSector === 'all' || company.sectors?.includes(selectedSector);

    return matchesSearch && matchesFund && matchesLocation && matchesStatus && matchesSector;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedFund('all');
    setSelectedLocation('all');
    setSelectedStatus('all');
    setSelectedSector('all');
  };

  const hasActiveFilters = searchTerm || selectedFund !== 'all' || selectedLocation !== 'all' || selectedStatus !== 'all' || selectedSector !== 'all';

  // Count companies by status for summary
  const activeCount = companies.filter(c => c.status === 'active').length;
  const exitedCount = companies.filter(c => c.status === 'exited').length;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-6">
              Our <span className="text-[#C8473A]">Portfolio</span>
            </h1>
            <div className="prose prose-lg max-w-none mb-6">
              <p className="text-gray-700 leading-relaxed mb-8">
                We invest in early-stage companies leveraging technology to build disruptive products. Our founders are typified as &apos;Challengineers&apos; – ethical entrepreneurs with the courage to question the status quo, and the resilience to persevere through adversity.
              </p>
              <p className="text-gray-700 leading-relaxed mb-0">
                Our focus areas in DeepTech and tech-enabled startups include AI, robotics, Industry 4.0, quantum computing, SpaceTech, etc, with strong IP possibilities.
              </p>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-600"><span className="font-semibold text-[#211F1F]">{activeCount}</span> Active</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#C8473A]"></span>
                <span className="text-gray-600"><span className="font-semibold text-[#211F1F]">{exitedCount}</span> Exited</span>
              </div>
            </div>
          </motion.div>

          {/* Search & Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6"
          >
            {/* Search Bar */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <label htmlFor="portfolio-search" className="sr-only">
                  Search portfolio companies
                </label>
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-light pointer-events-none" aria-hidden />
                <Input
                  id="portfolio-search"
                  placeholder="Search companies, sectors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-11 text-base rounded-xl border-gray-200"
                  data-testid="portfolio-search"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={`h-11 px-5 rounded-xl gap-2 ${showFilters ? 'bg-gray-50' : ''}`}
                data-testid="toggle-filters"
                aria-expanded={showFilters}
                aria-controls="portfolio-filters"
                aria-label={hasActiveFilters ? 'Show or hide portfolio filters (filters active)' : 'Show or hide portfolio filters'}
              >
                <Filter className="w-4 h-4" aria-hidden="true" />
                Filters
                {hasActiveFilters && (
                  <span className="w-2 h-2 rounded-full bg-[#C8473A]" aria-hidden="true"></span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} aria-hidden="true" />
              </Button>
            </div>

            {/* Filter Options */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  id="portfolio-filters"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                  aria-label="Portfolio filters"
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-gray-100 mt-4 w-full">
                    <Select value={selectedSector} onValueChange={setSelectedSector}>
                      <SelectTrigger data-testid="filter-sector" className="h-10 w-full min-w-0">
                        <SelectValue placeholder="Sector" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sectors</SelectItem>
                        {filters?.sectors?.map(sector => (
                          <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedFund} onValueChange={setSelectedFund}>
                      <SelectTrigger data-testid="filter-fund" className="h-10 w-full min-w-0">
                        <SelectValue placeholder="Fund" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Funds</SelectItem>
                        {filters?.funds?.map(fund => (
                          <SelectItem key={fund.fund_id} value={fund.fund_id}>
                            {fund.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger data-testid="filter-location" className="h-10 w-full min-w-0">
                        <SelectValue placeholder="Location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Locations</SelectItem>
                        {filters?.locations?.map(loc => (
                          <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                      <SelectTrigger data-testid="filter-status" className="h-10 w-full min-w-0">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="exited">Exited</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {hasActiveFilters && (
                    <div className="flex justify-end pt-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="text-gray-500 hover:text-[#C8473A]"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear all
                      </Button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-4 px-1">
            <p className="text-sm text-gray-500">
              Showing <span className="font-medium text-[#211F1F]">{filteredCompanies.length}</span> {filteredCompanies.length === 1 ? 'company' : 'companies'}
            </p>
          </div>

          {/* Portfolio List */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
            {loading ? (
              <div className="divide-y divide-gray-100">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="p-5 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gray-200 rounded-xl"></div>
                      <div className="flex-1">
                        <div className="h-5 bg-gray-200 rounded w-1/4 mb-2"></div>
                        <div className="h-4 bg-gray-100 rounded w-3/4 mb-2"></div>
                        <div className="flex gap-2">
                          <div className="h-5 bg-gray-100 rounded-full w-16"></div>
                          <div className="h-5 bg-gray-100 rounded-full w-20"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredCompanies.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <Building2 className="w-14 h-14 mx-auto text-gray-300 mb-4" />
                <h2 className="heading-empty">No companies found</h2>
                <p className="text-gray-500 mb-6">Try adjusting your search or filters</p>
                <Button variant="outline" onClick={clearFilters}>
                  Clear all filters
                </Button>
              </motion.div>
            ) : (
              <div data-testid="portfolio-list">
                {filteredCompanies.map((company, index) => (
                  <PortfolioRow 
                    key={company.company_id} 
                    company={company}
                    onClick={setSelectedCompany}
                    index={index}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      {/* Company Detail Modal */}
      <CompanyModal
        company={selectedCompany}
        isOpen={!!selectedCompany}
        onClose={() => setSelectedCompany(null)}
        fundNameById={fundNameById}
      />
    </div>
  );
};

export default PortfolioPage;
